import { useState } from "react";
import type { SyntheticEvent } from "react";
import { Globe2, Lock } from "lucide-react";
import { userReportsApi } from "../../api";
import type { GeneratedReportProposal, PreviewResult } from "../../api/userReports";
import { Button, DataTable, Modal } from "../ui";
import { getApiErrorMessage } from "../../utils";
import { useConfirm } from "../../hooks";
import type { UserReport } from "../../types/reports";

type ReportFormModalProps = {
  report?: UserReport | null;
  prefillSql?: string | null;
  prefill?: GeneratedReportProposal | null;
  onClose: () => void;
  onSuccess: (savedReport?: UserReport) => void;
};

export default function ReportFormModal({
  report = null,
  prefillSql = null,
  prefill = null,
  onClose,
  onSuccess,
}: ReportFormModalProps) {
  const isEdit = !!report;
  const { confirm, confirmDialog } = useConfirm();
  const [form, setForm] = useState({
    name: report?.name ?? prefill?.name ?? "",
    description: report?.description ?? prefill?.description ?? "",
    sql_query: report?.sql_query ?? prefill?.sql_query ?? prefillSql ?? "",
    is_public: report?.is_public ?? prefill?.is_public ?? false,
  });
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState("");
  const [testResult, setTestResult] = useState<PreviewResult | null>(null);

  function set(field: keyof typeof form, value: string | boolean) {
    setForm((f) => ({ ...f, [field]: value }));
    if (field === "sql_query") {
      setTestError("");
      setTestResult(null);
    }
  }

  async function testQuery() {
    const sql = form.sql_query.trim();
    if (!sql || testLoading) return;
    setTestLoading(true);
    setTestError("");
    setTestResult(null);
    try {
      const response = await userReportsApi.runSql(sql, { suppressErrorToast: true });
      setTestResult(response.data);
    } catch (caught) {
      setTestError(getApiErrorMessage(caught, "Could not run this query"));
    } finally {
      setTestLoading(false);
    }
  }

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    if (
      isEdit &&
      report?.is_public &&
      !form.is_public &&
      (report.subscriber_count ?? 0) > 0
    ) {
      const count = report.subscriber_count ?? 0;
      const confirmed = await confirm({
        title: "Make this report private?",
        message: `${count} ${count === 1 ? "subscription" : "subscriptions"} will be removed immediately. People with a direct share will keep access.`,
        confirmLabel: "Make private",
        danger: true,
      });
      if (!confirmed) return;
    }
    setLoading(true);
    try {
      let savedReport: UserReport;
      if (isEdit && report) {
        savedReport = (await userReportsApi.update(report.id, form)).data;
      } else {
        savedReport = (await userReportsApi.create(form)).data;
      }
      onSuccess(savedReport);
    } catch { return; } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Modal
        onClose={onClose}
        title={isEdit ? "Edit report" : "New report"}
        size="lg"
        onSubmit={submit}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : isEdit ? "Save changes" : "Create report"}
            </Button>
          </>
        }
      >
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
          <input required value={form.name} onChange={(e) => set("name", e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Description <span className="text-gray-400">(optional)</span></label>
          <textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)}
            className="w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between gap-3">
            <label htmlFor="report-sql-query" className="block text-sm font-medium text-gray-700">
              SQL query <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <Button
              variant="secondary"
              size="sm"
              onClick={testQuery}
              disabled={!form.sql_query.trim() || testLoading}
            >
              {testLoading ? "Testing…" : "Test query"}
            </Button>
          </div>
          <textarea
            id="report-sql-query"
            rows={6}
            value={form.sql_query}
            onChange={(e) => set("sql_query", e.target.value)}
            placeholder="SELECT …"
            spellCheck={false}
            className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-400">Read-only SELECT or WITH queries only.</p>
          {testError && <p role="alert" className="mt-2 text-sm text-red-600">{testError}</p>}
          {testResult && (
            <div className="mt-3">
              <p className="mb-1.5 text-xs text-gray-400">Preview · {testResult.row_count} rows</p>
              <DataTable rows={testResult.rows} dense maxHeight="14rem" />
            </div>
          )}
        </div>

        <fieldset>
          <legend className="mb-2 block text-sm font-medium text-gray-700">Visibility</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors ${!form.is_public ? "border-blue-500 bg-blue-50/50" : "border-gray-200 hover:border-gray-300"}`}>
              <input
                type="radio"
                name="report-visibility"
                value="private"
                checked={!form.is_public}
                onChange={() => set("is_public", false)}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900"><Lock size={14} aria-hidden="true" />Private</span>
                <span className="mt-1 block text-xs leading-4 text-gray-500">Only you and people you share it with can access this report</span>
              </span>
            </label>
            <label className={`flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors ${form.is_public ? "border-blue-500 bg-blue-50/50" : "border-gray-200 hover:border-gray-300"}`}>
              <input
                type="radio"
                name="report-visibility"
                value="public"
                checked={form.is_public}
                onChange={() => set("is_public", true)}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900"><Globe2 size={14} aria-hidden="true" />Public to Brian</span>
                <span className="mt-1 block text-xs leading-4 text-gray-500">Anyone signed in to Brian can find, preview, and subscribe</span>
              </span>
            </label>
          </div>
        </fieldset>
      </div>
      </Modal>
      {confirmDialog}
    </>
  );
}
